import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ChevronLeftIcon, LoaderIcon, PlusIcon } from '@/components/icons';
import { agentApi } from '@/api/agent.api';
import { toast } from 'sonner';
import { fullErrorText } from '@/api/errors';
import { applicationApi } from '@/api/application.api';
import { sandboxClassApi } from '@/api/sandboxClass.api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { create } from '@bufbuild/protobuf';
import { InstanceSchema } from '@/proto/agentplatform/v1/instance_pb';
import { ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import { ChannelType, ChannelAccountBindingMode } from '@/proto/agentplatform/v1/channel_pb';
import type { Param } from '@/proto/agentplatform/v1/param_pb';
import type { SandboxClass } from '@/proto/agentplatform/v1/sandbox_pb';
import ChannelParamInput from '@/components/Agents/ChannelParamInput';
import { CHANNEL_PARAM_DEFINITIONS } from '@/components/Agents/agentFormTypes';

function IconBox() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
      />
    </svg>
  );
}

function IconSliders() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
      />
    </svg>
  );
}

function SectionCard({
  id,
  icon,
  title,
  subtitle,
  children,
}: {
  id: string;
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-20">
      <div className="rounded-xl border border-border bg-background shadow-sm overflow-hidden">
        <div className="h-0.5 bg-gradient-to-r from-primary/80 via-primary/40 to-transparent" />
        <div className="p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10 text-primary shrink-0">
              {icon}
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">{title}</h2>
              {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
            </div>
          </div>
          {children}
        </div>
      </div>
    </section>
  );
}

function ParamInput({
  param,
  value,
  onChange,
}: {
  param: Param;
  value: string;
  onChange: (val: string) => void;
}) {
  const normalizedType = (param.type || '').toLowerCase();
  const isBooleanType = normalizedType === 'bool' || normalizedType === 'boolean';
  const isArrayType = normalizedType === 'array' || normalizedType.endsWith('[]');
  const defaultValue = param.paramValue?.value || '';

  const toArrayEditorText = (raw: string): string => {
    const source = (raw || defaultValue || '').trim();
    if (!source) {
      return '';
    }
    if (source.startsWith('[')) {
      try {
        const arr = JSON.parse(source);
        if (Array.isArray(arr)) {
          return arr.map((item) => String(item)).join('\n');
        }
      } catch {
        // fallback to plain split
      }
    }
    return source
      .split(/[\n,]/)
      .map((item) => item.trim())
      .filter((item) => item.length > 0)
      .join('\n');
  };

  const fromArrayEditorText = (raw: string): string => {
    const items = raw
      .split('\n')
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
    return JSON.stringify(items);
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">{param.name || param.target}</Label>
        <span className="text-[10px] font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
          {param.type}
        </span>
      </div>
      {isBooleanType ? (
        <select
          value={value || defaultValue || ''}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="">请选择</option>
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      ) : isArrayType ? (
        <>
          <textarea
            placeholder="每行输入一个值"
            value={toArrayEditorText(value)}
            onChange={(e) => onChange(fromArrayEditorText(e.target.value))}
            rows={3}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-y"
          />
          <p className="text-[10px] text-muted-foreground">每行一个元素，系统会自动保存为数组</p>
        </>
      ) : (
        <Input
          placeholder={defaultValue || '请输入值...'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 text-sm font-mono"
        />
      )}
    </div>
  );
}

function channelTypeLabel(channelType: ChannelType): string {
  switch (channelType) {
    case ChannelType.FEISHU:
      return '飞书';
    case ChannelType.QQ:
      return 'QQ';
    default:
      return ChannelType[channelType] ?? '未知渠道';
  }
}

export default function CreateApplicationPage() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [paramValues, setParamValues] = useState<Record<string, string>>({});
  const [pluginClassDetails, setPluginClassDetails] = useState<Record<string, SandboxClass>>({});

  const { data: agentsData, isLoading: agentsLoading } = useQuery({
    queryKey: ['agents-select'],
    queryFn: () => agentApi.listAgents(create(ListRequestSchema, { page: 1, pageSize: 100 })),
  });

  const { data: agentData, isLoading: agentDetailLoading } = useQuery({
    queryKey: ['agent-detail', selectedAgentId],
    queryFn: () => agentApi.getAgent(selectedAgentId),
    enabled: !!selectedAgentId,
  });

  useEffect(() => {
    if (agentData) {
      const defaults: Record<string, string> = {};

      agentData.modelConfig?.modelParams?.forEach((p) => {
        if (p.name) defaults[p.name] = p.paramValue?.value || '';
      });

      agentData.channelConfigs?.forEach((channel) => {
        channel.channelParams?.forEach((p) => {
          if (p.name) defaults[p.name] = p.paramValue?.value || '';
        });
      });

      agentData.pluginConfigs?.forEach((plugin) => {
        plugin.pluginParams?.forEach((p) => {
          if (p.name) defaults[p.name] = p.paramValue?.value || '';
        });
      });

      setParamValues(defaults);

      const classUids = (agentData.pluginConfigs || [])
        .filter((p) => p.pluginType === 'sandboxClass' && p.pluginUid)
        .map((p) => p.pluginUid);

      if (classUids.length > 0) {
        Promise.all(
          classUids.map((id) => sandboxClassApi.getSandboxClass(id).catch(() => null))
        ).then((results) => {
          const details: Record<string, SandboxClass> = {};
          results.forEach((data, i) => {
            if (data) details[classUids[i]] = data;
          });
          setPluginClassDetails(details);
        });
      } else {
        setPluginClassDetails({});
      }
    } else {
      setParamValues({});
      setPluginClassDetails({});
    }
  }, [agentData]);

  const createMutation = useMutation({
    mutationFn: applicationApi.create,
    onSuccess: () => navigate('/applications'),
    onError: (error) => toast.error(`创建失败：${fullErrorText(error)}`),
  });

  const handleSubmit = () => {
    if (!selectedAgentId || !name) return;
    createMutation.mutate(
      create(InstanceSchema, {
        name,
        description,
        agentId: selectedAgentId,
        params: paramValues,
      })
    );
  };

  const isPending = createMutation.isPending;

  const modelParams = agentData?.modelConfig?.modelParams || [];
  const channelConfigs = (agentData?.channelConfigs || []).filter(
    (c) => c.bindingMode !== ChannelAccountBindingMode.TEMPLATE
  );
  const pluginConfigs = agentData?.pluginConfigs || [];
  const hasParams =
    modelParams.length > 0 ||
    channelConfigs.some((channel) => (channel.channelParams?.length || 0) > 0) ||
    pluginConfigs.some((p) => p.pluginParams?.length > 0);

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="w-full px-6 pt-8 pb-4">
        <button
          onClick={() => navigate('/applications')}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ChevronLeftIcon className="h-4 w-4" />
          返回智能体实例列表
        </button>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground tracking-tight">创建智能体实例</h1>
            <p className="text-sm text-muted-foreground mt-1">
              选择智能体模版并配置参数，创建运行实例
            </p>
          </div>
        </div>
      </div>

      <div className="w-full px-6 pb-16">
        <div className="space-y-5">
          <SectionCard
            id="basic"
            icon={<IconBox />}
            title="实例信息"
            subtitle="选择模版并填写实例基本信息"
          >
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="agent" className="text-sm font-medium">
                  智能体模版 <span className="text-destructive">*</span>
                </Label>
                <select
                  id="agent"
                  value={selectedAgentId}
                  onChange={(e) => setSelectedAgentId(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">选择模版...</option>
                  {agentsLoading ? (
                    <option disabled>加载中...</option>
                  ) : (
                    agentsData?.data?.map((agent) => (
                      <option key={agent.id} value={agent.id}>
                        {agent.name}
                      </option>
                    ))
                  )}
                </select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-sm font-medium">
                  实例名称 <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  placeholder="例如: 客服机器人-生产环境"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-10"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="desc" className="text-sm font-medium">
                  描述
                </Label>
                <textarea
                  id="desc"
                  placeholder="简要描述此实例的用途..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
                />
              </div>
            </div>
          </SectionCard>

          {selectedAgentId && agentDetailLoading && (
            <div className="flex items-center justify-center py-8">
              <LoaderIcon className="h-5 w-5 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">加载模版参数...</span>
            </div>
          )}

          {selectedAgentId && !agentDetailLoading && hasParams && (
            <SectionCard
              id="params"
              icon={<IconSliders />}
              title="参数配置"
              subtitle="配置模版定义的参数值"
            >
              <div className="space-y-6">
                {modelParams.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-foreground">模型参数</h3>
                    <div className="space-y-3">
                      {modelParams.map((param) => (
                        <ParamInput
                          key={`model.${param.name || 'unknown'}`}
                          param={param}
                          value={paramValues[param.name || ''] || ''}
                          onChange={(val) =>
                            setParamValues((prev) => ({ ...prev, [param.name || '']: val }))
                          }
                        />
                      ))}
                    </div>
                  </div>
                )}

                {channelConfigs
                  .filter((channel) => (channel.channelParams?.length || 0) > 0)
                  .map((channel, channelIndex) => (
                    <div key={`channel.${channelIndex}`} className="space-y-3">
                      <h3 className="text-sm font-semibold text-foreground">
                        渠道参数（{channelTypeLabel(channel.channelType)}）
                      </h3>
                      <div className="space-y-3">
                        {channel.channelParams.map((param) => {
                          const paramDefs = CHANNEL_PARAM_DEFINITIONS[channel.channelType] ?? [];
                          const paramDef = paramDefs.find((d) => d.name === param.name);
                          if (paramDef) {
                            return (
                              <ChannelParamInput
                                key={`channel.${channelIndex}.${param.name || 'unknown'}`}
                                def={paramDef}
                                value={paramValues[param.name || ''] || ''}
                                onChange={(val) =>
                                  setParamValues((prev) => ({
                                    ...prev,
                                    [param.name || '']: val,
                                  }))
                                }
                              />
                            );
                          }
                          return (
                            <ParamInput
                              key={`channel.${channelIndex}.${param.name || 'unknown'}`}
                              param={param}
                              value={paramValues[param.name || ''] || ''}
                              onChange={(val) =>
                                setParamValues((prev) => ({
                                  ...prev,
                                  [param.name || '']: val,
                                }))
                              }
                            />
                          );
                        })}
                      </div>
                    </div>
                  ))}

                {pluginConfigs.map((plugin, idx) =>
                  plugin.pluginParams?.length > 0 ? (
                    <div key={idx} className="space-y-3">
                      <div className="pb-2 border-b border-border/50">
                        <div className="flex items-center gap-2">
                          <span
                            className={`shrink-0 px-2 py-0.5 rounded text-[10px] font-medium ${
                              plugin.pluginType === 'sandboxClass'
                                ? 'bg-blue-500/10 text-blue-600'
                                : 'bg-green-500/10 text-green-600'
                            }`}
                          >
                            {plugin.pluginType === 'sandboxClass' ? '沙箱模版' : '沙箱实例'}
                          </span>
                          <span className="text-sm font-semibold text-foreground">
                            {pluginClassDetails[plugin.pluginUid]?.name ||
                              plugin.name ||
                              plugin.pluginType}
                          </span>
                        </div>
                        {plugin.pluginType === 'sandboxClass' &&
                          pluginClassDetails[plugin.pluginUid]?.description && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {pluginClassDetails[plugin.pluginUid].description}
                            </p>
                          )}
                      </div>
                      <div className="space-y-3">
                        {plugin.pluginParams.map((param) => (
                          <ParamInput
                            key={`plugin.${idx}.${param.name}`}
                            param={param}
                            value={paramValues[param.name || ''] || ''}
                            onChange={(val) =>
                              setParamValues((prev) => ({
                                ...prev,
                                [param.name || '']: val,
                              }))
                            }
                          />
                        ))}
                      </div>
                    </div>
                  ) : null
                )}
              </div>
            </SectionCard>
          )}

          {selectedAgentId && !agentDetailLoading && !hasParams && (
            <div className="rounded-xl border border-border bg-background shadow-sm p-6">
              <p className="text-sm text-muted-foreground text-center">该模版未定义可配置参数</p>
            </div>
          )}

          <div className="pt-4">
            <Button
              onClick={handleSubmit}
              disabled={isPending || agentsLoading || !selectedAgentId || !name}
              className="w-full h-11 gap-2 text-base"
            >
              {isPending ? (
                <>
                  <LoaderIcon className="h-4 w-4 animate-spin" />
                  创建中...
                </>
              ) : (
                <>
                  <PlusIcon className="h-4 w-4" />
                  创建实例
                </>
              )}
            </Button>
            {(!selectedAgentId || !name) && (
              <p className="text-xs text-muted-foreground text-center mt-2">
                请选择智能体模版并填写实例名称
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
